import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const initial = { name: '', email: '', company: '', message: '' };

export const ContactForm = ({ t, labels, placeholders }) => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle');

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('request failed');
      setStatus('success');
      setForm(initial);
    } catch (err) {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="box-line p-10 md:p-14 relative"
        data-testid="contact-success"
      >
        <motion.span
          initial={{ scale: 0, rotate: -15 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 180, delay: 0.15 }}
          className="inline-flex items-center justify-center w-14 h-14 bg-lime text-ink font-display text-2xl mb-6 border border-ink"
          aria-hidden
        >
          &#10003;
        </motion.span>
        <h3 className="display text-4xl md:text-5xl text-ink leading-[0.95] mb-4">{t('contact.success')}</h3>
        <p className="serif-italic text-lg text-ink/80 leading-snug max-w-lg">{t('contact.subhead')}</p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-8 font-mono uppercase text-xs tracking-widest text-ink link-underline"
          data-testid="contact-send-another"
        >
          {t('contact.submit')} ↗
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={onSubmit}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="box-line p-6 md:p-10 flex flex-col gap-4"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5" htmlFor="cf-name">{labels.name}</label>
          <input id="cf-name" type="text" required value={form.name} onChange={update('name')} placeholder={placeholders.name} className="input-mono" data-testid="contact-input-name" />
        </div>
        <div>
          <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5" htmlFor="cf-email">{labels.email}</label>
          <input id="cf-email" type="email" required value={form.email} onChange={update('email')} placeholder={placeholders.email} className="input-mono" data-testid="contact-input-email" />
        </div>
      </div>

      <div>
        <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5" htmlFor="cf-company">{labels.company}</label>
        <input id="cf-company" type="text" value={form.company} onChange={update('company')} placeholder={placeholders.company} className="input-mono" data-testid="contact-input-company" />
      </div>

      <div>
        <label className="font-mono uppercase text-[10px] tracking-widest2 text-ink/70 block mb-1.5" htmlFor="cf-message">{labels.message}</label>
        <textarea id="cf-message" required value={form.message} onChange={update('message')} placeholder={placeholders.message} rows={4} className="input-mono resize-none" data-testid="contact-input-message" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <p className="font-mono uppercase text-[10px] tracking-widest2 text-ink/60">
          {t('contact.metaLine')}
        </p>
        <button
          type="submit"
          disabled={status === 'sending'}
          className="group inline-flex items-center gap-2 bg-ink text-page px-6 py-4 font-mono uppercase tracking-widest text-xs border border-ink hover:bg-lime hover:text-ink transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="contact-submit"
        >
          {status === 'sending' ? t('contact.sending') : t('contact.submit')}
          <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-0.5">↗</span>
        </button>
      </div>

      <AnimatePresence>
        {status === 'error' && (
          <motion.p
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="font-mono uppercase text-xs tracking-widest2 text-clay pt-2"
            role="alert"
            data-testid="contact-error"
          >
            {t('contact.errorGeneric')}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.form>
  );
};
